/**
 * Componente de banner do estabelecimento
 */

import type { EstablishmentPublic } from "../types";
import { resolveImageUrl, hexToRgba } from "../utils";
import { DEFAULT_BRAND_HEX, resolveReadableTextColor } from "../../../lib/brandScale";
import { FiMapPin } from "react-icons/fi";
import { SocialLinks } from "./SocialLinks";
import { BranchSwitcher } from "./BranchSwitcher";
import type { PublicBranch } from "../../../services/public";

interface EstablishmentBannerProps {
  establishment: EstablishmentPublic;
  bannerStyle: React.CSSProperties;
  onImageClick?: () => void;
  branches?: PublicBranch[];
  onBranchChange?: (slug: string) => void;
}

function clampNumber(value: unknown, min: number, max: number, fallback: number): number {
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return fallback;
  return Math.min(max, Math.max(min, numberValue));
}

function formatBranchLabel(value: string) {
  return String(value || "")
    .replace(/\[.*?\]\s*/g, "")
    .replace(/\s*unidade\s*/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (char) => char.toUpperCase());
}

function normalizeBranchPart(value: unknown) {
  return String(value || "")
    .trim()
    .toLocaleLowerCase("pt-BR")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getBranchLocation(branch: PublicBranch) {
  return [branch.city, branch.state].filter(Boolean).join(" - ");
}

function getBranchIdentityKey(branch: PublicBranch) {
  return [
    normalizeBranchPart(formatBranchLabel(branch.name || "")),
    normalizeBranchPart(branch.city),
    normalizeBranchPart(branch.state),
  ].join("|");
}

function branchHasLocation(branch: PublicBranch) {
  return Boolean(normalizeBranchPart(branch.city) || normalizeBranchPart(branch.state));
}

function getVisibleBranches(branches: PublicBranch[], currentSlug: string) {
  const groups = new Map<string, PublicBranch[]>();

  for (const branch of branches) {
    if (!String(branch.name || "").trim()) continue;
    const nameKey = normalizeBranchPart(formatBranchLabel(branch.name || ""));
    groups.set(nameKey, [...(groups.get(nameKey) || []), branch]);
  }

  const visibleBranches: PublicBranch[] = [];

  for (const group of groups.values()) {
    const branchesWithLocation = group.filter(branchHasLocation);
    const candidates = branchesWithLocation.length > 0 ? branchesWithLocation : group;
    const byIdentity = new Map<string, PublicBranch>();

    for (const branch of candidates) {
      const key = getBranchIdentityKey(branch);
      const existing = byIdentity.get(key);
      const isCurrent = String(branch.slug || "").trim() === currentSlug;

      if (!existing || isCurrent) {
        byIdentity.set(key, branch);
      }
    }

    visibleBranches.push(...byIdentity.values());
  }

  return visibleBranches;
}

export function EstablishmentBanner({
  establishment,
  bannerStyle,
  onImageClick,
  branches = [],
  onBranchChange,
}: EstablishmentBannerProps) {
  const currentSlug = String(establishment.slug || "").trim();
  const hasCurrentBranchInList = branches.some(
    (branch) => String(branch.slug || "").trim() === currentSlug
  );

  const dropdownBranches: PublicBranch[] = hasCurrentBranchInList
    ? branches
    : [
        {
          id: establishment.id,
          name: establishment.name,
          slug: establishment.slug,
          city: establishment.city || null,
          state: establishment.state || null,
          isCurrent: true,
        },
        ...branches,
      ];
  const visibleBranches = getVisibleBranches(dropdownBranches, currentSlug);
  const canSwitchBranch = visibleBranches.length > 1;
  const hasLogo = Boolean(establishment.logoUrl);

  /**
   * Sem cor de texto escolhida, deriva uma legivel sobre o fundo. So vale
   * quando o fundo e uma cor forte: com imagem, o degrade escuro por cima ja
   * garante o texto claro. Quem escolheu cor de proposito e respeitado.
   */
  const derivedTextColor = establishment.backgroundImageUrl
    ? null
    : resolveReadableTextColor(establishment.backgroundColor);
  const titleColor = establishment.headerTitleColor || derivedTextColor || "#F8FAFC";
  const descriptionColor =
    establishment.headerDescriptionColor || derivedTextColor || "#F8FAFC";
  const addressColor = establishment.infoBoxTextColor || descriptionColor;
  const bannerOverlayOpacity = clampNumber(
    establishment.bannerOverlayOpacity,
    0,
    0.7,
    0.2
  );
  const bannerOverlayBase = establishment.backgroundColor || DEFAULT_BRAND_HEX;
  const bannerTint =
    hexToRgba(bannerOverlayBase, establishment.backgroundColor ? 0.28 : 0.18) ||
    hexToRgba(DEFAULT_BRAND_HEX, 0.18)!;
  const bannerShadow =
    hexToRgba(bannerOverlayBase, establishment.backgroundColor ? 0.7 : 0.32) ||
    hexToRgba(DEFAULT_BRAND_HEX, 0.32)!;

  const addressLine = [establishment.addressLine, establishment.addressNumber]
    .filter(Boolean)
    .join(", ");
  const locationLine = [
    establishment.neighborhood,
    establishment.city,
    establishment.state,
  ]
    .filter(Boolean)
    .join(" - ");
  const aboutText =
    establishment.aboutText ||
    "Ambiente tranquilo, profissionais especialistas e horários pensados para sua rotina.";

  return (
    // App instalado no iPhone usa `black-translucent`: a página desenha sob a
    // barra de status. O topo reserva `env(safe-area-inset-top)` para o relógio
    // não ficar sobre o logo; no navegador comum o inset é 0 e nada muda.
    <header
      className="relative overflow-hidden border-b border-white/10 px-4 pb-5 pt-[calc(1.25rem+env(safe-area-inset-top))] text-white md:px-6 lg:pb-4 lg:pt-[calc(1rem+env(safe-area-inset-top))]"
      style={bannerStyle}
      onClick={onImageClick}
    >
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `linear-gradient(90deg, ${bannerTint}, transparent 46%, ${bannerShadow})`,
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ backgroundColor: `rgba(0, 0, 0, ${bannerOverlayOpacity})` }}
      />

      <div
        className={`relative mx-auto grid w-full max-w-[1320px] items-center gap-4 ${
          hasLogo
            ? "md:grid-cols-[112px_minmax(0,1fr)] lg:grid-cols-[118px_minmax(0,1fr)_260px] xl:grid-cols-[128px_minmax(0,1fr)_300px]"
            : "md:grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_340px]"
        }`}
      >
        {hasLogo ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={resolveImageUrl(establishment.logoUrl)}
            alt={`Logo de ${establishment.name}`}
            className="h-24 w-24 rounded-2xl border border-white/25 bg-white object-cover p-1 shadow-xl md:h-28 md:w-28 lg:h-[112px] lg:w-[112px] xl:h-[120px] xl:w-[120px]"
            onError={(event) => {
              event.currentTarget.style.display = "none";
            }}
          />
        ) : null}

        <div className={`min-w-0 ${hasLogo && !canSwitchBranch ? "lg:col-span-2" : ""}`}>
          <h1
            className="break-words text-[38px] font-black leading-[1.02] tracking-tight md:text-[44px] lg:text-[42px] xl:text-[48px]"
            style={{ color: titleColor }}
          >
            {establishment.name}
          </h1>

          <p
            className="mt-2 max-w-[760px] text-[17px] font-medium leading-snug md:text-[18px]"
            style={{ color: descriptionColor }}
          >
            {aboutText}
          </p>

          {(addressLine || locationLine) && (
            <div
              className="mt-3 max-w-[760px] text-[14px] font-medium leading-relaxed"
              style={{ color: addressColor, opacity: 0.9 }}
            >
              <p className="flex items-start gap-2" style={{ color: addressColor }}>
                <FiMapPin
                  className="mt-1 h-4 w-4 flex-shrink-0"
                  style={{ color: addressColor }}
                  aria-hidden="true"
                />
                <span style={{ color: addressColor }}>
                  {addressLine}
                  {addressLine && locationLine ? <br /> : null}
                  {locationLine}
                </span>
              </p>
            </div>
          )}

          <SocialLinks establishment={establishment} textColor={descriptionColor} />
        </div>

        {canSwitchBranch && (
          <BranchSwitcher
            branches={visibleBranches}
            currentSlug={currentSlug}
            labelColor={descriptionColor}
            formatName={formatBranchLabel}
            getLocation={getBranchLocation}
            onBranchChange={onBranchChange}
          />
        )}
      </div>
    </header>
  );
}
